/**
 * Dashboard - ViTex
 * Side-rail nav on desktop, top bar + drawer on mobile.
 * Active tab lives in the URL (?tab=...) so deep links and redirects work.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  LogOut, FileText, Clock, Download, CheckCircle, CreditCard,
  AlertCircle, LayoutDashboard, ShoppingBag, User as UserIcon, Zap,
  Menu, X, Mail, ArrowRight, Sparkles,
} from 'lucide-react';
import PaymentModal from '../components/PaymentModal';
import Footer from '../components/Footer';
import Logo from '../components/Logo';
import { transcriptionsAPI, ordersAPI, plansAPI, authAPI } from '../services/api';
import { useI18n } from '../i18n/I18nContext';
import { DOWNLOADS } from '../config/downloads';
import '../styles/Dashboard.css';

// Plans are fully dynamic — features and "popular" flag come from the DB.
const parsePlanFeatures = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const parsed = JSON.parse(raw); return Array.isArray(parsed) ? parsed : []; }
    catch { return []; }
  }
  return [];
};

const formatDate = (iso, locale) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(locale === 'tr' ? 'tr-TR' : 'en-US');
  } catch {
    return iso;
  }
};

const formatAmount = (amount) => {
  if (amount == null) return '—';
  const n = Number(amount);
  return Number.isFinite(n) ? `₺${n.toFixed(2)}` : `${amount}`;
};

const VALID_TABS = ['overview', 'history', 'orders', 'plans', 'account'];

const Dashboard = () => {
  const { user, logout, refreshUser } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  const [transcriptions, setTranscriptions] = useState([]);
  const [transcriptionsLoading, setTranscriptionsLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [plans, setPlans] = useState([]);

  const TABS = [
    { id: 'overview', label: t('dashboard.tabs.overview'), icon: LayoutDashboard },
    { id: 'history',  label: t('dashboard.tabs.history'),  icon: FileText },
    { id: 'orders',   label: t('dashboard.tabs.orders'),   icon: ShoppingBag },
    { id: 'plans',    label: t('dashboard.tabs.plans'),    icon: Zap },
    { id: 'account',  label: t('dashboard.tabs.account'),  icon: UserIcon },
  ];

  useEffect(() => {
    refreshUser().catch((e) => console.error('Error refreshing user data:', e));

    transcriptionsAPI.list()
      .then((res) => setTranscriptions(Array.isArray(res?.data) ? res.data : []))
      .catch((e) => { console.error('Error fetching transcriptions:', e); setTranscriptions([]); })
      .finally(() => setTranscriptionsLoading(false));

    ordersAPI.list()
      .then((res) => setOrders(Array.isArray(res?.data) ? res.data : []))
      .catch((e) => { console.error('Error fetching orders:', e); setOrders([]); })
      .finally(() => setOrdersLoading(false));

    plansAPI.list()
      .then((res) => setPlans(Array.isArray(res?.data) ? res.data : []))
      .catch((e) => { console.error('Error fetching plans:', e); setPlans([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep ?tab=... and the active state in sync — covers /profile redirects and
  // back/forward navigation between tabs.
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (VALID_TABS.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const goToTab = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
    setDrawerOpen(false);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleUpgradePlan = (planSlug) => {
    if (user?.account_type === planSlug) {
      alert(t('dashboard.plans.alreadyOn', { plan: planSlug }));
      return;
    }
    setSelectedPlan(planSlug);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async () => {
    await refreshUser();
    setShowPaymentModal(false);
    try {
      const res = await ordersAPI.list();
      setOrders(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('Error refreshing orders:', e);
    }
  };

  const recentTranscriptions = transcriptions.slice(0, 5);

  return (
    <>
      <div className="page-shell">
      <div className="dash-shell">
        {/* MOBILE TOP BAR — visible only <768px via CSS */}
        <header className="dash-mobile-bar">
          <button
            type="button"
            className="dash-burger"
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu size={22} />
          </button>
          <div className="dash-mobile-logo">
            <Logo height={30} />
          </div>
        </header>

        {/* OVERLAY behind drawer */}
        {drawerOpen && (
          <div
            className="dash-overlay"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* SIDEBAR — fixed left on desktop, drawer on mobile */}
        <aside className={`dash-sidebar ${drawerOpen ? 'open' : ''}`}>
          <div className="dash-sidebar-top">
            <div className="dash-logo">
              <Logo height={34} />
            </div>
            <button
              type="button"
              className="dash-close"
              aria-label="Close menu"
              onClick={() => setDrawerOpen(false)}
            >
              <X size={22} />
            </button>
          </div>

          <div className="dash-user-card">
            <div className="dash-user-avatar">
              {(user?.name || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="dash-user-meta">
              <strong>{user?.name || 'User'}</strong>
              <span>{user?.email}</span>
            </div>
          </div>

          <nav className="dash-nav" aria-label="Dashboard sections">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={`dash-nav-item ${activeTab === id ? 'active' : ''}`}
                onClick={() => goToTab(id)}
              >
                <Icon size={18} />
                <span>{label}</span>
              </button>
            ))}
          </nav>

          <div className="dash-sidebar-bottom">
            <button onClick={handleLogout} className="dash-logout">
              <LogOut size={18} />
              <span>{t('dashboard.logout')}</span>
            </button>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="dash-main">
          {activeTab === 'overview' && (
            <OverviewTab
              user={user}
              transcriptionsCount={transcriptions.length}
              recentTranscriptions={recentTranscriptions}
              transcriptionsLoading={transcriptionsLoading}
              onGoToHistory={() => goToTab('history')}
              onGoToPlans={() => goToTab('plans')}
              t={t}
              lang={lang}
            />
          )}

          {activeTab === 'history' && (
            <HistoryTab
              transcriptions={transcriptions}
              loading={transcriptionsLoading}
              t={t}
              lang={lang}
            />
          )}

          {activeTab === 'orders' && (
            <OrdersTab
              orders={orders}
              loading={ordersLoading}
              onGoToPlans={() => goToTab('plans')}
              t={t}
              lang={lang}
            />
          )}

          {activeTab === 'plans' && (
            <PlansTab
              user={user}
              plans={plans}
              onUpgrade={handleUpgradePlan}
              t={t}
            />
          )}

          {activeTab === 'account' && (
            <AccountTab
              user={user}
              onEmailUpdated={refreshUser}
              t={t}
            />
          )}
        </main>
      </div>

      <Footer />
      </div>

      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          user={user}
          plan={selectedPlan}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </>
  );
};

// ─── OVERVIEW ────────────────────────────────────────────────────────────────

const OverviewTab = ({
  user, transcriptionsCount, recentTranscriptions, transcriptionsLoading,
  onGoToHistory, onGoToPlans, t, lang,
}) => {
  const remaining = Math.max(0, (user?.total_minutes || 0) - (user?.used_minutes || 0));
  const usedPct = user?.total_minutes
    ? Math.min(100, Math.round(((user.used_minutes || 0) / user.total_minutes) * 100))
    : 0;

  return (
    <>
      <div className="dash-page-head">
        <h1>{t('dashboard.welcome')} {user?.name || 'User'}</h1>
        <p>{t('dashboard.recent.title')}</p>
      </div>

      <div className="dash-stats">
        <div className="dash-stat">
          <div className="dash-stat-icon" style={{ background: 'rgba(0,219,168,0.1)', color: '#00dba8' }}>
            <FileText size={22} />
          </div>
          <div className="dash-stat-body">
            <span className="dash-stat-label">{t('dashboard.stats.totalTranscriptions')}</span>
            <span className="dash-stat-value">{transcriptionsCount}</span>
          </div>
        </div>

        <div className="dash-stat">
          <div className="dash-stat-icon" style={{ background: 'rgba(0,185,154,0.1)', color: '#00c4a0' }}>
            <Clock size={22} />
          </div>
          <div className="dash-stat-body">
            <span className="dash-stat-label">{t('dashboard.stats.minutesUsed')}</span>
            <span className="dash-stat-value">{user?.used_minutes || 0} / {user?.total_minutes || 0}</span>
            <div className="dash-stat-bar" aria-hidden="true">
              <div className="dash-stat-bar-fill" style={{ width: `${usedPct}%` }} />
            </div>
            <span className="dash-stat-hint">{remaining} {t('dashboard.account.minutesSuffix')}</span>
          </div>
        </div>

        <div className="dash-stat">
          <div className="dash-stat-icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#fbbf24' }}>
            <CreditCard size={22} />
          </div>
          <div className="dash-stat-body">
            <span className="dash-stat-label">{t('dashboard.stats.accountType')}</span>
            <span className="dash-stat-value plan-badge">{user?.account_type?.toUpperCase() || t('dashboard.stats.freeTrial')}</span>
            <button className="dash-stat-link" onClick={onGoToPlans}>
              {t('dashboard.stats.upgrade')} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Quick actions — plans + downloads up top so they aren't buried */}
      <div className="dash-quick">
        <div className="dash-quick-card dash-quick-plans">
          <div className="dash-quick-icon"><Sparkles size={24} /></div>
          <div className="dash-quick-body">
            <h3>{t('dashboard.plans.title')}</h3>
            <p>{t('dashboard.plans.subtitle')}</p>
            <button className="dash-cta-primary" onClick={onGoToPlans}>
              {t('dashboard.plans.selectPlan')} <ArrowRight size={16} />
            </button>
          </div>
        </div>

        <div className="dash-quick-card dash-quick-download">
          <div className="dash-quick-icon"><Download size={24} /></div>
          <div className="dash-quick-body">
            <h3>{t('dashboard.download.cardTitle')}</h3>
            <p>{t('dashboard.download.cardDesc')}</p>
            <div className="dash-download-row">
              <a
                href={DOWNLOADS.mac.url}
                className="dash-cta-ghost"
                download={DOWNLOADS.mac.filename}
              >
                <AppleIcon /> macOS
              </a>
              <a
                href={DOWNLOADS.win.url}
                className="dash-cta-ghost"
                download={DOWNLOADS.win.filename}
              >
                <WindowsIcon /> Windows
              </a>
            </div>
          </div>
        </div>
      </div>

      <section className="dash-section">
        <div className="dash-section-head">
          <h2>{t('dashboard.recent.title')}</h2>
          {recentTranscriptions.length > 0 && (
            <button className="dash-text-btn" onClick={onGoToHistory}>
              {t('dashboard.recent.viewAll')} <ArrowRight size={14} />
            </button>
          )}
        </div>
        {transcriptionsLoading ? (
          <div className="dash-empty">{t('common.loading')}</div>
        ) : recentTranscriptions.length === 0 ? (
          <div className="dash-empty dash-empty-icon">
            <AlertCircle size={28} />
            <p>{t('dashboard.recent.empty')}</p>
          </div>
        ) : (
          <TranscriptionsTable rows={recentTranscriptions} t={t} lang={lang} />
        )}
      </section>
    </>
  );
};

// ─── HISTORY ─────────────────────────────────────────────────────────────────

const HistoryTab = ({ transcriptions, loading, t, lang }) => (
  <>
    <div className="dash-page-head">
      <h1>{t('dashboard.history.title')}</h1>
    </div>
    <section className="dash-section">
      {loading ? (
        <div className="dash-empty">{t('common.loading')}</div>
      ) : transcriptions.length === 0 ? (
        <div className="dash-empty dash-empty-icon">
          <AlertCircle size={28} />
          <p>{t('dashboard.history.empty')}</p>
        </div>
      ) : (
        <TranscriptionsTable rows={transcriptions} t={t} lang={lang} />
      )}
    </section>
  </>
);

const TranscriptionsTable = ({ rows, t, lang }) => (
  <div className="dash-table-wrap">
    <table className="dash-table">
      <thead>
        <tr>
          <th>{t('dashboard.history.colFile')}</th>
          <th>{t('dashboard.history.colDuration')}</th>
          <th>{t('dashboard.history.colLanguage')}</th>
          <th>{t('dashboard.history.colStatus')}</th>
          <th>{t('dashboard.history.colDate')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td className="dash-cell-file" title={row.file_name}>{row.file_name}</td>
            <td>{row.duration_minutes} {t('dashboard.account.minutesSuffix')}</td>
            <td>{row.language || '—'}</td>
            <td>
              <span className={`dash-pill dash-pill-${row.status || 'success'}`}>
                {row.status || 'success'}
              </span>
            </td>
            <td>{formatDate(row.created_at, lang)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── ORDERS ──────────────────────────────────────────────────────────────────

const OrdersTab = ({ orders, loading, onGoToPlans, t, lang }) => (
  <>
    <div className="dash-page-head dash-page-head-row">
      <h1>{t('dashboard.orders.title')}</h1>
      <button className="dash-text-btn" onClick={onGoToPlans}>
        {t('dashboard.orders.buyMore')} <ArrowRight size={14} />
      </button>
    </div>
    <section className="dash-section">
      {loading ? (
        <div className="dash-empty">{t('common.loading')}</div>
      ) : orders.length === 0 ? (
        <div className="dash-empty dash-empty-icon">
          <AlertCircle size={28} />
          <p>{t('dashboard.orders.empty')}</p>
        </div>
      ) : (
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>{t('dashboard.orders.colDate')}</th>
                <th>{t('dashboard.orders.colPlan')}</th>
                <th>{t('dashboard.orders.colAmount')}</th>
                <th>{t('dashboard.orders.colStatus')}</th>
                <th>{t('dashboard.orders.colPaymentId')}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td>{formatDate(o.created_at, lang)}</td>
                  <td style={{ textTransform: 'capitalize' }}>{o.plan_type}</td>
                  <td>{formatAmount(o.amount)}</td>
                  <td>
                    <span className={`dash-pill dash-pill-${o.status}`}>{o.status}</span>
                  </td>
                  <td className="dash-cell-mono" title={o.iyzico_payment_id || ''}>
                    {o.iyzico_payment_id || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  </>
);

// ─── PLANS ───────────────────────────────────────────────────────────────────

const PlansTab = ({ user, plans, onUpgrade, t }) => {
  const purchasable = (plans || []).filter((p) => p.is_active && p.is_purchasable);

  return (
    <>
      <div className="dash-page-head">
        <h1>{t('dashboard.plans.title')}</h1>
        <p>{t('dashboard.plans.subtitle')}</p>
      </div>

      <div className="pricing-cards">
        {purchasable.length === 0 ? (
          <div className="dash-empty">{t('common.loading') || 'Loading…'}</div>
        ) : purchasable.map((p) => {
          const features = parsePlanFeatures(p.features);
          const popular = !!p.is_popular;
          const isCurrent = user?.account_type === p.slug;
          const priceStr = Number(p.price).toFixed(0);
          return (
            <div
              key={p.id}
              className={`pricing-card ${popular ? 'popular' : ''} ${isCurrent ? 'current-plan' : ''}`}
            >
              {popular && <div className="popular-badge">{t('dashboard.plans.mostPopular')}</div>}
              {isCurrent && <div className="current-badge">{t('dashboard.plans.currentBadge')}</div>}

              <div className="pricing-header">
                <h3>{p.name}</h3>
                <div className="price-container">
                  <span className="currency">₺</span>
                  <span className="price">{priceStr}</span>
                  <span className="duration">{t('dashboard.plans.perMinutes', { minutes: p.minutes })}</span>
                </div>
              </div>

              <div className="pricing-features">
                {features.map((f, i) => (
                  <div key={i} className="pricing-feature">
                    <CheckCircle size={18} color="#00dba8" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>

              <button
                className={`pricing-btn ${isCurrent ? 'current-btn' : popular ? 'primary-btn' : 'secondary-btn'}`}
                onClick={() => onUpgrade(p.slug)}
                disabled={isCurrent}
              >
                {isCurrent ? t('dashboard.plans.currentBtn') : popular ? t('dashboard.plans.upgradeNow') : t('dashboard.plans.selectPlan')}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
};

// ─── ACCOUNT (email change + password change) ────────────────────────────────

const PasswordChangeCard = ({ t }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const reset = () => {
    setOpen(false);
    setForm({ current_password: '', new_password: '', confirm: '' });
    setError(''); setInfo('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (form.new_password !== form.confirm) {
      setError(t('dashboard.account.passwordMismatch'));
      return;
    }
    setBusy(true);
    try {
      await authAPI.changePassword({
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setInfo(t('dashboard.account.passwordUpdated'));
      setForm({ current_password: '', new_password: '', confirm: '' });
      setTimeout(() => setOpen(false), 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="dash-email-current">
        <div>
          <span className="dash-email-label">{t('dashboard.account.changePasswordTitle')}</span>
          <strong>••••••••</strong>
        </div>
        <button className="dash-cta-ghost" onClick={() => setOpen(true)}>
          {t('dashboard.account.changePasswordTitle')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="dash-form">
      <label>{t('dashboard.account.currentPasswordLabel')}</label>
      <input
        type="password"
        name="current_password"
        value={form.current_password}
        onChange={change}
        autoComplete="current-password"
        required
      />
      <label>{t('dashboard.account.newPasswordLabel')}</label>
      <input
        type="password"
        name="new_password"
        value={form.new_password}
        onChange={change}
        autoComplete="new-password"
        minLength={8}
        required
      />
      <label>{t('dashboard.account.confirmPasswordLabel')}</label>
      <input
        type="password"
        name="confirm"
        value={form.confirm}
        onChange={change}
        autoComplete="new-password"
        minLength={8}
        required
      />
      <p className="dash-form-hint">{t('dashboard.account.passwordRule')}</p>
      {info && <div className="dash-form-info">{info}</div>}
      {error && <div className="dash-form-error">{error}</div>}
      <div className="dash-form-actions">
        <button type="button" className="dash-cta-ghost" onClick={reset} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="dash-cta-primary" disabled={busy}>
          {busy ? '…' : t('dashboard.account.updatePasswordBtn')}
        </button>
      </div>
    </form>
  );
};

const AccountTab = ({ user, onEmailUpdated, t }) => {
  // step: 'idle' → 'form' → 'verify' → 'idle'
  const [step, setStep] = useState('idle');
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Tick down the resend cooldown once per second while > 0. Cleared on unmount.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const reset = () => {
    setStep('idle');
    setNewEmail('');
    setPassword('');
    setOtp('');
    setPendingEmail('');
    setError('');
    setInfo('');
    setCooldown(0);
  };

  const requestChange = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    setBusy(true);
    try {
      const res = await authAPI.requestEmailChange({ new_email: newEmail, password });
      setPendingEmail(res?.data?.new_email || newEmail);
      setPassword('');
      setStep('verify');
      setInfo(t('dashboard.account.emailCodeSent'));
      setCooldown(Number(res?.data?.cooldown_seconds) || 60);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (cooldown > 0) return;
    setError(''); setInfo('');
    setBusy(true);
    try {
      const res = await authAPI.resendEmailChange();
      setInfo(t('dashboard.account.codeResent'));
      setCooldown(Number(res?.data?.cooldown_seconds) || 60);
    } catch (e) {
      // 429 carries a number we can use directly so the countdown shows the
      // real remaining time (not 60).
      const data = e.response && e.response.data;
      if (data && data.cooldown_seconds) setCooldown(Number(data.cooldown_seconds));
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyChange = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    setBusy(true);
    try {
      await authAPI.verifyEmailChange(otp.trim());
      setInfo(t('dashboard.account.emailUpdated'));
      if (onEmailUpdated) await onEmailUpdated();
      setTimeout(reset, 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="dash-page-head">
        <h1>{t('dashboard.account.title')}</h1>
      </div>

      <section className="dash-section">
        <div className="dash-info-grid">
          <div><span>{t('dashboard.account.name')}</span><strong>{user?.name || '—'}</strong></div>
          <div>
            <span>{t('dashboard.account.email')}</span>
            <strong>{user?.email || '—'}</strong>
          </div>
          <div>
            <span>{t('dashboard.account.accountType')}</span>
            <strong className={`info-value badge ${user?.account_type || 'starter'}`}>
              {user?.account_type?.toUpperCase() || 'STARTER'}
            </strong>
          </div>
          <div>
            <span>{t('dashboard.account.remaining')}</span>
            <strong>
              {(user?.total_minutes || 0) - (user?.used_minutes || 0)} {t('dashboard.account.minutesSuffix')}
            </strong>
          </div>
          <div>
            <span>{t('dashboard.account.status')}</span>
            <strong className="info-value badge-success">{t('common.active')}</strong>
          </div>
        </div>
      </section>

      <section className="dash-section">
        <div className="dash-section-head">
          <h2><Mail size={18} style={{ verticalAlign: '-3px', marginRight: 6 }} />
            {t('dashboard.account.changeEmailTitle')}
          </h2>
        </div>

        {step === 'idle' && (
          <div className="dash-email-current">
            <div>
              <span className="dash-email-label">{t('dashboard.account.email')}</span>
              <strong>{user?.email}</strong>
            </div>
            <button className="dash-cta-ghost" onClick={() => setStep('form')}>
              {t('dashboard.account.changeEmailBtn')}
            </button>
          </div>
        )}

        {step === 'form' && (
          <form onSubmit={requestChange} className="dash-form">
            <label>{t('dashboard.account.newEmailLabel')}</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <label>{t('dashboard.account.passwordLabel')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <p className="dash-form-hint">{t('dashboard.account.changeEmailHint')}</p>
            {error && <div className="dash-form-error">{error}</div>}
            <div className="dash-form-actions">
              <button type="button" className="dash-cta-ghost" onClick={reset} disabled={busy}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="dash-cta-primary" disabled={busy}>
                {busy ? '…' : t('dashboard.account.sendCodeBtn')}
              </button>
            </div>
          </form>
        )}

        {step === 'verify' && (
          <form onSubmit={verifyChange} className="dash-form">
            <p className="dash-form-hint">
              {t('dashboard.account.codeSentTo')} <strong>{pendingEmail}</strong>.
            </p>
            <label>{t('dashboard.account.codeLabel')}</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              className="dash-otp-input"
              autoFocus
              required
            />
            {info && <div className="dash-form-info">{info}</div>}
            {error && <div className="dash-form-error">{error}</div>}
            <div className="dash-form-resend">
              <button
                type="button"
                className="dash-text-btn"
                onClick={resendCode}
                disabled={busy || cooldown > 0}
              >
                {cooldown > 0
                  ? t('dashboard.account.resendInSeconds', { seconds: cooldown })
                  : t('dashboard.account.resendCodeBtn')}
              </button>
            </div>
            <div className="dash-form-actions">
              <button type="button" className="dash-cta-ghost" onClick={reset} disabled={busy}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="dash-cta-primary" disabled={busy || otp.length !== 6}>
                {busy ? '…' : t('dashboard.account.verifyBtn')}
              </button>
            </div>
          </form>
        )}
      </section>

      <section className="dash-section">
        <div className="dash-section-head">
          <h2>{t('dashboard.account.changePasswordTitle')}</h2>
        </div>
        <PasswordChangeCard t={t} />
      </section>
    </>
  );
};

// ─── Small inline SVGs so we don't pull more icon deps ──────────────────────

const WindowsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M0,0H11.377V11.372H0ZM12.623,0H24V11.372H12.623ZM0,12.623H11.377V24H0ZM12.623,12.623H24V24H12.623Z" />
  </svg>
);

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

export default Dashboard;
